import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

const FlightOfferUiContext = createContext(null);

/** Fallback ref when provider missing — never triggers gated close logic */
const flightEditorCloseAfterLoadingFallbackRef = { current: false };

/**
 * מצב תצוגה לזרימת טיסות ב-searchoffer: מסך תוצאות immersive (בלי top nav / בלי מנוע חיפוש כפול).
 * flightSearchEditorOpen: "ערוך חיפוש" פתוח מתוך סרגל התוצאות — להסתיר כרום משני (סיכום חי וכו׳).
 *
 * flightEditorCloseAfterLoadingCycleRef: מסומן לפני submit מתוך עורך התוצאות — מאפשר לסגור את העורך
 * רק אחרי מחזור טעינה שמסתיים (לא על כל loading גלובלי).
 */
export function FlightOfferUiProvider({ children }) {
  const [immersiveFlightResults, setImmersiveFlightResultsState] = useState(false);
  const [flightSearchEditorOpen, setFlightSearchEditorOpenState] = useState(false);
  const flightEditorCloseAfterLoadingCycleRef = useRef(false);

  const setImmersiveFlightResults = useCallback((v) => {
    setImmersiveFlightResultsState(!!v);
  }, []);

  const setFlightSearchEditorOpen = useCallback((v) => {
    setFlightSearchEditorOpenState(!!v);
  }, []);

  const markFlightEditorSearchSubmittedFromResultsBar = useCallback(() => {
    flightEditorCloseAfterLoadingCycleRef.current = true;
  }, []);

  const clearFlightEditorPendingCloseAfterSearch = useCallback(() => {
    flightEditorCloseAfterLoadingCycleRef.current = false;
  }, []);

  const value = useMemo(
    () => ({
      immersiveFlightResults,
      setImmersiveFlightResults,
      flightSearchEditorOpen,
      setFlightSearchEditorOpen,
      flightEditorCloseAfterLoadingCycleRef,
      markFlightEditorSearchSubmittedFromResultsBar,
      clearFlightEditorPendingCloseAfterSearch,
    }),
    [
      immersiveFlightResults,
      setImmersiveFlightResults,
      flightSearchEditorOpen,
      setFlightSearchEditorOpen,
      markFlightEditorSearchSubmittedFromResultsBar,
      clearFlightEditorPendingCloseAfterSearch,
    ]
  );

  return (
    <FlightOfferUiContext.Provider value={value}>
      {children}
    </FlightOfferUiContext.Provider>
  );
}

export function useFlightOfferUi() {
  const ctx = useContext(FlightOfferUiContext);
  if (!ctx) {
    return {
      immersiveFlightResults: false,
      setImmersiveFlightResults: () => {},
      flightSearchEditorOpen: false,
      setFlightSearchEditorOpen: () => {},
      flightEditorCloseAfterLoadingCycleRef: flightEditorCloseAfterLoadingFallbackRef,
      markFlightEditorSearchSubmittedFromResultsBar: () => {},
      clearFlightEditorPendingCloseAfterSearch: () => {},
    };
  }
  return ctx;
}
